import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Calendar, Gift, Star, User, LogOut, Award, Wallet, Smartphone } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

export default function Account() {
  const { t, localize, fmtSAR, fmtDate, isAr } = useI18n();
  const { user, isCustomer, logout, refresh, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({});
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (!isCustomer) return;
    api.get("/me/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/me/loyalty").then((r) => setLoyalty(r.data)).catch(() => {});
    setProfile(user || {});
  }, [isCustomer, user]);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!isCustomer) nav("/admin");
  }, [user, isCustomer, nav, loading]);
  if (loading) return null;
  if (!user || !isCustomer) return null;

  const cancel = async (id) => {
    try { const { data } = await api.post(`/bookings/${id}/cancel`); toast.success(`${isAr ? "تم الإلغاء، الاسترداد" : "Cancelled, refund"}: ${fmtSAR(data.refund_amount)}`); api.get("/me/bookings").then((r) => setBookings(r.data)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveProfile = async () => {
    try { await api.put("/auth/profile", profile); await refresh(); toast.success(t("common.save")); }
    catch (e) { toast.error(apiError(e)); }
  };

  const statusColor = (s) => ({ confirmed: "bg-green-100 text-green-700", pending_payment: "bg-amber-100 text-amber-700", completed: "bg-blue-100 text-blue-700", cancelled: "bg-red-100 text-red-700" }[s] || "bg-slate-100 text-slate-600");

  const tabs = [["bookings", Calendar, t("account.bookings")], ["orders", Gift, t("account.orders")], ["loyalty", Award, t("account.loyalty")], ["profile", User, t("account.profile")]];

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-8">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-slate-900">{t("account.title")}</h1><p className="text-sm text-slate-500">{user.name || user.phone}</p></div>
        <button onClick={() => { logout(); nav("/"); }} data-testid="account-logout" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm text-[hsl(var(--brand-red))] hover:bg-red-50"><LogOut className="h-4 w-4" /> {t("nav.logout")}</button>
      </div>

      <div className="mt-6 flex gap-1 overflow-x-auto no-scrollbar border-b border-border">
        {tabs.map(([k, Icon, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`} className={`flex shrink-0 items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-medium ${tab === k ? "border-primary text-primary" : "border-transparent text-slate-500"}`}><Icon className="h-4 w-4" /> {label}</button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "bookings" && (bookings.length === 0 ? <Empty text={t("account.no_bookings")} /> : (
          <div className="space-y-3">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`account-booking-${b.id}`} className="rounded-xl border border-border bg-white p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{fmtDate(b.start).greg} · {fmtDate(b.start).time}</div>
                    <div className="text-xs text-slate-400">{fmtDate(b.start).hijri}</div>
                    <div className="mt-1 text-sm text-slate-500">{t("common.total")}: {fmtSAR(b.amount_total)} · {t("account.paid")}: {fmtSAR(b.amount_paid)}</div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusColor(b.status)}`}>{b.status}</span>
                </div>
                {!["cancelled", "completed"].includes(b.status) && <button onClick={() => cancel(b.id)} data-testid={`cancel-booking-${b.id}`} className="mt-3 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-[hsl(var(--brand-red))] hover:bg-red-50">{t("account.cancel_booking")}</button>}
              </div>
            ))}
          </div>
        ))}
        {tab === "orders" && (orders.length === 0 ? <Empty text={t("common.none")} /> : (
          <div className="space-y-3">{orders.map((o) => (<div key={o.id} className="flex justify-between rounded-xl border border-border bg-white p-4"><span className="text-sm">{o.items?.map((i) => localize(i, "name")).join("، ")}</span><span className="font-semibold text-primary">{fmtSAR(o.total)}</span></div>))}</div>
        ))}
        {tab === "loyalty" && (
          <div>
            <div className="mx-auto max-w-sm">
              <div id="loyalty-card" data-testid="loyalty-card" className="relative overflow-hidden rounded-3xl bg-slate-950 p-6 text-white shadow-xl">
                <div className="hero-flare absolute inset-0" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <img src="/logo-motawahj.png" alt="" className="h-8 rounded bg-white p-1" />
                    <span className="rounded-full bg-[hsl(var(--brand-yellow))] px-3 py-1 text-xs font-bold text-slate-900 uppercase">{loyalty?.tier || "bronze"}</span>
                  </div>
                  <div className="mt-6 text-4xl font-extrabold">{loyalty?.points ?? (user.loyalty_points || 0)}</div>
                  <div className="text-sm text-slate-300">{t("account.points")}</div>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="text-slate-300">{user.name || user.phone}</span>
                    <span className="font-mono text-slate-400">#{loyalty?.member_id || user.id?.slice(0, 8).toUpperCase()}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-border bg-white p-4">
                <div className="flex items-center justify-between text-sm"><span className="text-slate-600">{isAr ? "التقدّم للجلسة المجانية" : "Progress to free session"}</span><span className="font-bold text-primary">{loyalty?.progress_to_free ?? 0}/{loyalty?.free_every ?? 3}</span></div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full bg-[hsl(var(--brand-orange))]" style={{ width: `${((loyalty?.progress_to_free ?? 0) / (loyalty?.free_every ?? 3)) * 100}%` }} /></div>
              </div>
              <div className="mt-4 flex flex-col items-center rounded-2xl border border-border bg-white p-4">
                <QRCodeCanvas value={`MOTAWAHJ:${loyalty?.member_id || user.id}`} size={132} level="M" data-testid="loyalty-qr" />
                <p className="mt-2 text-xs text-slate-400">{isAr ? "اعرض الرمز في الفرع لتجميع نقاطك" : "Show this code at the branch to earn points"}</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button onClick={async () => {
                  try {
                    const r = await api.get("/wallet/apple-pass", { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a"); a.href = url; a.download = "motawahj.pkpass";
                    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                  } catch (e) { toast.info(isAr ? "Apple Wallet — يتطلب شهادات Apple Developer (وضع تجريبي)" : "Apple Wallet — requires Apple Developer certs (demo)"); }
                }} data-testid="apple-wallet-btn" aria-label="Add to Apple Wallet" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 py-2.5 text-xs font-semibold text-white"><Wallet className="h-4 w-4" /> Apple Wallet</button>
                <button onClick={async () => {
                  try {
                    const r = await api.post("/wallet/loyalty-link");
                    if (r.data.configured && r.data.save_url) { window.location.assign(r.data.save_url); }
                    else { toast.info(isAr ? "Google Wallet — يتطلب تفعيل حساب المُصدر (وضع تجريبي)" : "Google Wallet — requires issuer setup (demo)"); }
                  } catch (e) { toast.error(isAr ? "تعذّر إنشاء البطاقة" : "Could not create pass"); }
                }} data-testid="google-wallet-btn" aria-label="Save to Google Wallet" className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border py-2.5 text-xs font-semibold text-slate-700"><Smartphone className="h-4 w-4" /> Google Wallet</button>
              </div>
              <button onClick={() => window.print()} data-testid="save-loyalty-card" className="mt-3 w-full rounded-full bg-primary py-3 font-semibold text-white">{isAr ? "احفظ البطاقة على جهازك" : "Save card to device"}</button>
            </div>
            {loyalty?.rewards?.length > 0 && (
              <div className="mx-auto mt-6 max-w-sm space-y-2">
                <h3 className="font-semibold text-slate-900">{isAr ? "مكافآتك" : "Your rewards"}</h3>
                {loyalty.rewards.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-xl border border-border bg-white p-3 text-sm">
                    <span className="inline-flex items-center gap-2"><Award className="h-4 w-4 text-[hsl(var(--brand-yellow))]" /> {r.type === "free_session" ? (isAr ? "جلسة مجانية" : "Free session") : r.type}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${r.status === "available" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mx-auto mt-6 max-w-sm rounded-xl bg-primary/5 p-4 text-center text-sm text-primary">{isAr ? "كل 3 جلسات مدفوعة مكتملة = جلسة مجانية 🎉" : "Every 3 completed paid sessions = 1 free session 🎉"}</div>
          </div>
        )}
        {tab === "profile" && (
          <div className="max-w-md space-y-4">
            {[["name", t("common.name")], ["email", t("common.email")], ["birthday", isAr ? "تاريخ الميلاد" : "Birthday"], ["anniversary", isAr ? "المناسبة السنوية" : "Anniversary"]].map(([k, label]) => (
              <div key={k}><label className="text-sm font-medium text-slate-700">{label}</label><input value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} data-testid={`profile-${k}`} className="mt-1 w-full rounded-xl border border-border px-4 py-3" /></div>
            ))}
            <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={!!profile.marketing_consent} onChange={(e) => setProfile({ ...profile, marketing_consent: e.target.checked })} className="h-4 w-4" /> {isAr ? "أوافق على استقبال العروض التسويقية" : "I consent to marketing messages"}</label>
            <button onClick={saveProfile} data-testid="profile-save" className="rounded-full bg-primary px-6 py-2.5 font-semibold text-white">{t("common.save")}</button>
          </div>
        )}
      </div>
    </div>
  );
}

const Empty = ({ text }) => <div className="rounded-2xl border border-dashed border-border py-16 text-center text-slate-400">{text}</div>;
